import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'nl' | 'en';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
  localePath: (path: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  nl: {
    lang_toggle: 'NL / EN',
    nav_cases: 'Cases',
    nav_articles: 'Artikelen',
    nav_about: 'Over ons',
    nav_method: 'Werkwijze',
    nav_signal: 'Signal',
    nav_pricing: 'Pricing',
    nav_contact: 'Contact',
    growth_scan_btn: 'Gratis Growth Scan',
    thanks_title: 'Bedankt voor je aanvraag!',
    thanks_desc: 'We hebben je aanvraag ontvangen en nemen binnen 24 uur contact met je op om je Gratis AI Growth Scan in te plannen.',
    thanks_sub: 'In de tussentijd: check hoe wij als digital marketing bureau Vedder & Vedder hielpen om 5 jaar op rij 100% te groeien.',
    view_case: 'Bekijk de case',
    hero_title_1: 'We laten merken groeien <span class="text-[#5CE698]">&amp;</span> bouwen de toekomst van D2C.',
    hero_title_2: '',
    hero_desc: 'Eén partner voor advertising, CRM, creative en data.',
    hero_desc_bold: 'Wrkt',
    hero_desc_2: 'schaalt D2C-merken met senior expertise en eigen AI-tooling. Voorspelbaar en meetbaar.',
    hero_cta: 'Plan een kennismaking',
    hero_cta2: 'Doe de gratis Growth Scan',
    hero_stat_1: '100+ MERKEN HELPEN GROEIEN',
    hero_stat_2: '15 JAAR ERVARING',
    hero_stat_3: '24/7 AI-OPTIMALISATIE',
    trusted_by: 'Deze brands gingen je voor',
    prob_label: 'Herkenbaar?',
    prob_title_1: 'Je ad spend stijgt. Je ROAS daalt. En niemand kan uitleggen',
    prob_title_2: 'waarom',
    prob_title_3: '.',
    prob_p1: 'Je marketingteam draait campagnes, maar mist het overzicht. Welke creatives presteren? Wat doet je concurrent anders? Waar lekt je budget?',
    prob_p2_1: 'Zonder data-gedreven analyse blijf je gokken - en betaal je de prijs.',
    prob_p2_2: 'Dat is precies waar wij inspringen.',
    fw_label: 'Ons Framework',
    fw_title_1: 'De Growth Compass: van data naar resultaat in 4',
    fw_title_2: 'stappen',
    step1_t: 'Scan & Analyse',
    step1_d: 'We duiken in je campagnes, creatives en concurrenten. Geen aannames, maar feiten.',
    step2_t: 'Strategie',
    step2_d: 'We bouwen een groeiplan met duidelijke doelen, kanalen en budgetverdeling.',
    step3_t: 'Executie',
    step3_d: 'Campagnes gaan live op META, TikTok, Pinterest en meer. Onze AI optimaliseert continu.',
    step4_t: 'Groei & Retentie',
    step4_d: 'Wekelijkse rapportage, nieuwe klanten én herhaalaankopen. Groei die blijft.',
    proof_label: 'Resultaten',
    proof_title_1: 'Merken die we hielpen',
    proof_title_2: 'groeien.',
    view_all_cases: 'Bekijk alle cases',
    case_franky_desc: '5 jaar partner - elk jaar meer dan verdubbeld in omzet door full-funnel aanpak.',
    case_feather_desc: 'Van nul naar marktspeler in een drukke beauty markt. D2C lancering en doorgroei.',
    case_mimi_desc: 'Amsterdams sieradenmerk - structureel opgeschaald met data als fundament.',
    case_amaya_desc: '3 jaar partner - van eerste online verkoop naar voorspelbare, schaalbare groei.',
    case_jl_desc: 'Campagnestrategie voor Nederland\'s grootste juridische dienstverlener.',
    kpi_revenue: 'Omzet',
    kpi_new_customers: 'Nieuwe klanten',
    signal_label: 'Competitive Intelligence',
    signal_title_1_1: 'Je concurrent test 47 creatives per maand.',
    signal_title_1_2: 'Jij 3. Dat is het',
    signal_title_2: 'verschil',
    signal_title_3: '.',
    signal_p1: 'Terwijl jij je volgende campagne plant, weet je concurrent al wat werkt - en schaalt het op. Met onze competitive intelligence zie je precies welke creatives, hooks en formats werken in jouw markt.',
    signal_p2: '',
    signal_b1_t: 'Zie wat je concurrenten doen',
    signal_b1_d: 'Welke ads draaien ze? Welke formats converteren? Je krijgt direct inzicht.',
    signal_b2_t: 'AI herkent wat mensen missen',
    signal_b2_d: 'Patronen in hooks, visuals en copy die het verschil maken tussen 2x en 0,5x ROAS.',
    signal_b3_t: 'Actiepunten, geen rapporten',
    signal_b3_d: 'Concrete stappen die je deze week kunt toepassen. Geen PowerPoints.',
    cta_badge: 'Klaar om te groeien?',
    cta_title: 'Ontdek wat je laat liggen.',
    cta_desc: 'Vraag de gratis Growth Scan aan. Je ziet waar je grootste groeikansen liggen, wat er in je markt gebeurt en welke stappen je het snelst vooruit helpen.',
    faq_title: 'Veelgestelde vragen',
    faq_q1: 'Wat maakt Wrkt anders dan andere bureaus?',
    faq_a1: 'Wrkt combineert senior expertise met eigen AI-tooling. Voordat we één euro uitgeven analyseren we je creatives, de strategieën van je concurrenten in de Meta Ad Library en je volledige funnel. We starten dus met data. Gecombineerd met hands-on expertise op Meta, TikTok, Pinterest, Google Ads en Klaviyo bouwen we een groei-engine op maat voor jouw merk.',
    faq_q2: 'Hoe werkt de samenwerking in de praktijk?',
    faq_a2: 'Het begint met een kennismaking van 30 minuten met een senior strateeg die vooraf al naar je merk heeft gekeken. Past het van beide kanten, dan duiken we in je cijfers en markt en komen we terug met een concreet groeiplan. Liever eerst zelf inzicht? Start dan met de gratis Growth Scan; die analyseert je creatives, concurrenten en funnel-setup en is een sterke opmaat naar het gesprek.',
    faq_q3: 'Op welke kanalen zijn jullie actief?',
    faq_a3: 'We werken op de kanalen die de meeste groei opleveren voor D2C-merken: Meta (Facebook & Instagram), TikTok, Pinterest, Google Ads en Klaviyo voor e-mail en retentie. We focussen bewust op de kanalen waar jouw doelgroep zit en waar we de meeste impact maken. Welke kanalen voor jouw merk het meest logisch zijn, bespreken we in de Growth Review.',
    faq_q4: 'Hoe snel kan ik resultaten verwachten?',
    faq_a4: 'De eerste optimalisaties gaan binnen week één live. De meeste merken zien meetbare verbeteringen binnen 30 dagen en significante groei binnen 60 tot 90 dagen. De exacte tijdlijn hangt af van je startpunt - een merk met sterke creatives maar slechte tracking ziet sneller resultaat dan een merk dat een volledige funnel-rebuild nodig heeft. We zijn transparant over wat je kunt verwachten en wanneer tijdens je Growth Review.',
    faq_q5: 'Is dit geschikt voor mijn merk?',
    faq_a5: 'Wrkt werkt uitsluitend met D2C e-commerce merken die klaar zijn voor de volgende stap in groei en advertising. We houden ons klantenportfolio bewust klein, zodat we elke klant echte diepgang en aandacht kunnen geven. Twijfel je of we bij elkaar passen? De Growth Scan is de beste manier om erachter te komen: gratis en vrijblijvend.',
    faq_q6: 'Wat kost de Growth Scan?',
    faq_a6: 'Niets. De Growth Scan is volledig gratis. We investeren de tijd in het analyseren van je creatives, concurrenten en setup omdat we alleen merken aannemen waar we er zeker van zijn dat we echte impact kunnen maken. Zie het als een manier voor beide kanten om te ontdekken of er een match is - volledig vrijblijvend.',
    faq_q7: 'Wat kost een samenwerking met Wrkt?',
    faq_a7: 'Onze samenwerkingen starten vanaf €2.500 per maand, afhankelijk van je omzetfase en het aantal kanalen. Na je Growth Review stellen we een voorstel op maat samen, precies met wat jouw merk in deze fase nodig heeft. We doen liever minder dingen met echte impact dan alles een beetje.',
    faq_q8: 'Hoe lang is het contract?',
    faq_a8: 'We starten met een minimale periode van drie maanden. Dat is de tijd die we nodig hebben om het fundament te leggen - tracking inrichten, creative strategie ontwikkelen, campagnes lanceren en optimaliseren op basis van echte data. Na de startperiode loopt de samenwerking maandelijks door. We geloven in het elke maand verdienen van je vertrouwen, niet in je vastzetten.',
    footer_desc: 'Growth agency voor D2C e-commerce. Senior expertise, eigen AI-tooling. Amsterdam. Meta Business Partner.',
    footer_partners: 'Official Partners',
    footer_contact: 'Contact',
    footer_links: 'Links',
    footer_cases: 'Cases',
    footer_privacy: 'Privacy Policy',
    footer_rights: 'All rights reserved.',
    contact_title: 'Praat met Robin, niet met sales',
    contact_desc: 'Je spreekt meteen Robin de Wildt: we bespreken je uitdagingen, kijken naar je cijfers en bepalen samen of er een klik is.',
    contact_info_title: 'Direct inplannen',
    contact_address: 'Herengracht 372, 1016 CH Amsterdam',
    contact_email: 'contact@wrkt.nl',
    contact_phone: '+31 (0)20 896 51 36',
    contact_form_name: 'Naam',
    contact_form_email: 'Zakelijk E-mailadres',
    contact_form_company: 'Bedrijfsnaam of Website',
    contact_form_budget: 'Waar heb je ons gevonden?',
    contact_form_message: 'Bericht (Optioneel)',
    contact_form_submit: 'Verstuur Aanvraag',
    contact_success_title: 'Bedankt voor je aanvraag',
    contact_success_desc: 'We nemen zo snel mogelijk contact met je op.',
    contact_step1_t: '1. Discovery Call',
    contact_step1_d: 'We bespreken je huidige e-commerce doelen, obstakels en kijken of er een klik is.',
    contact_step2_t: '2. Account Audit',
    contact_step2_d: 'Jij geeft ons inzage, wij maken een diepgaande analyse van je campagnes en marktkansen.',
    contact_step3_t: '3. Strategie Sessie',
    contact_step3_d: 'We presenteren onze bevindingen en de concrete stappen voor hogere ROAS.',
    contact_step4_t: '4. Voorstel',
    contact_step4_d: 'Je ontvangt een helder actieplan om je doelen te behalen met ons dedicated team.',
    contact_step5_t: '5. Launch & Scale',
    contact_step5_d: 'Na akkoord lanceren we de nieuwe campagnes en starten we de 24/7 AI-optimalisatie.',
    about_title_1: 'Maak kennis met',
    about_title_2: 'Wrkt Digital',
    about_subtitle: 'Experts in het strategisch groeien van D2C brands.',
    about_p1: 'Wrkt Digital is opgericht met één overtuiging: groei hoort voorspelbaar en meetbaar te zijn. We combineren 10 jaar Wrkt en 15+ jaar performance-expertise met eigen AI-tooling en een senior team dat e-commerce door en door kent.',
    about_p2: 'Je werkt direct met je strateeg, volgt je campagnes dagelijks in je eigen dashboard en weet elke maand precies wat de volgende stap is.',
    about_stat1_val: '2017',
    about_stat1_label: 'OPGERICHT',
    about_stat2_val: '5',
    about_stat2_label: 'SPECIALISTEN',
    about_stat3_val: '100+',
    about_stat3_label: 'MERKEN GEHOLPEN',
    about_stat4_val: '0',
    about_stat4_label: 'ACCOUNT MANAGERS',
    about_team_title: 'Ons team',
    about_team_desc: 'Klein, snel en hands-on. We werken vanuit de Herengracht in Amsterdam. Iedereen in het team werkt direct aan klantcampagnes - geen tussenlagen.',
    about_robin_role: 'Founder & Growth Strategist',
    about_robin_desc: '15 jaar in e-commerce. Heeft meer dan 50 D2C merken geholpen schalen. Denkt in data, handelt op kansen.',
    about_team_member2: 'Performance Marketing',
    about_team_member3: 'Creative Strategy',
    about_team_member4: 'AI & Data Engineering',
    about_location_title: 'Herengracht, Amsterdam',
    about_location_desc: 'Ons kantoor zit op de Herengracht 372 in Amsterdam. Loop gerust binnen voor een kop koffie en een goed gesprek over groei.',
    about_cta: 'Neem contact op',
    scan_landing_badge: '2 minuten · Gratis · Direct resultaat',
    scan_landing_title_1: 'Hoe ver is',
    scan_landing_title_2: 'jouw merk',
    scan_landing_title_3: 'van echte',
    scan_landing_title_4: 'groei?',
    scan_landing_desc_1: 'Doe de ',
    scan_landing_desc_bold: 'WRKT Growth Scan',
    scan_landing_desc_2: ': 5 vragen over je merk + een directe scan van je website op SEO, socials, tracking en techniek.',
    scan_landing_list_1: 'SEO & meta tags check van je website',
    scan_landing_list_2: 'Social media kanalen & tracking pixels detectie',
    scan_landing_list_3: 'Technische setup beoordeling',
    scan_landing_list_4: 'Persoonlijke groei-score met verbeterpunten',
    scan_start_btn: 'Start de Growth Scan',
    scan_q1_cat: 'Jouw Merk',
    scan_q1_title: 'In welke categorie zit jouw merk?',
    scan_q1_sub: 'We specialiseren ons in lifestyle verticals - zo geven we je de meest relevante benchmarks.',
    scan_q1_o1_l: 'Fashion & Sieraden',
    scan_q1_o1_d: 'Kleding, accessoires, juwelen, horloges',
    scan_q1_o2_l: 'Beauty & Verzorging',
    scan_q1_o2_d: 'Cosmetica, skincare, haircare, wellness',
    scan_q1_o3_l: 'Home & Living',
    scan_q1_o3_d: 'Interieur, wonen, lifestyle producten',
    scan_q1_o4_l: 'Food & Health',
    scan_q1_o4_d: 'Voeding, supplements, gezonde levensstijl',
    scan_q1_o5_l: 'Andere D2C categorie',
    scan_q1_o5_d: 'Direct-to-consumer, maar anders',
    scan_q2_cat: 'Omzet & Ambitie',
    scan_q2_title: 'Wat is je huidige jaaromzet?',
    scan_q2_sub: 'Eerlijk zijn loont - we stemmen het advies af op waar je nu écht staat.',
    scan_q2_o1_l: 'Onder €100K',
    scan_q2_o1_d: 'Vroege fase, eerste tractie opbouwen',
    scan_q2_o2_l: '€100K – €500K',
    scan_q2_o2_d: 'Groeiende basis, eerste schaalervaringen',
    scan_q2_o3_l: '€500K – €2M',
    scan_q2_o3_d: 'Serieuze tractie, klaar om te versnellen',
    scan_q2_o4_l: '€2M – €10M',
    scan_q2_o4_d: 'Gevestigd merk, schaal is het doel',
    scan_q2_o5_l: 'Boven €10M',
    scan_q2_o5_d: 'Enterprise-niveau, verdere groei',
    scan_q2_insight: 'De meeste lifestyle merken schalen het snelst tussen €500K en €5M jaaromzet. Dit is het moment waarop paid advertising het grootste hefboomeffect heeft.',
    scan_q3_cat: 'Kanalen',
    scan_q3_title: 'Welke kanalen gebruik je nu actief?',
    scan_q3_sub: 'Selecteer alles wat je momenteel inzet. We kijken daarna naar wat er ontbreekt.',
    scan_q3_multi_hint: 'Meerdere antwoorden mogelijk.',
    scan_q3_o1_l: 'Meta Ads',
    scan_q3_o1_d: 'Facebook & Instagram advertenties',
    scan_q3_o2_l: 'Google Ads',
    scan_q3_o2_d: 'Search, Shopping, Performance Max',
    scan_q3_o3_l: 'TikTok Ads',
    scan_q3_o3_d: 'Short-form video advertising',
    scan_q3_o4_l: 'Pinterest Ads',
    scan_q3_o4_d: 'Visual discovery platform',
    scan_q3_o5_l: 'E-mail / Klaviyo',
    scan_q3_o5_d: 'Nieuwsbrieven, flows, automations',
    scan_q3_o6_l: 'Influencer / UGC',
    scan_q3_o6_d: 'Creator content & partnerships',
    scan_q3_o7_l: 'Nog geen betaald',
    scan_q3_o7_d: 'Alleen organisch of nog niet actief',
    scan_q4_cat: 'Media Budget',
    scan_q4_title: 'Hoeveel investeer je maandelijks in paid media?',
    scan_q4_sub: 'Je advertentiebudget bepaalt welke strategie en schaalsnelheid realistisch voor je is.',
    scan_q4_o1_l: 'Minder dan €1.000',
    scan_q4_o1_d: 'Eerste stappen, nog aan het testen',
    scan_q4_o2_l: '€1.000 – €5.000',
    scan_q4_o2_d: 'Actief testend, resultaten komen',
    scan_q4_o3_l: '€5.000 – €20.000',
    scan_q4_o3_d: 'Serieus budget, rendement verwacht',
    scan_q4_o4_l: '€20.000 – €75.000',
    scan_q4_o4_d: 'Grote speler, schaalambities',
    scan_q4_o5_l: 'Boven €75.000',
    scan_q4_o5_d: 'Enterprise-niveau investering',
    scan_q4_insight: 'Met minder dan €1K/maand is het lastig om significante data te verzamelen. Merken die écht schalen starten bij €3–5K/maand als minimum inzet.',
    scan_q5_cat: 'Pijnpunt',
    scan_q5_title: 'Wat houdt jouw groei het meest tegen?',
    scan_q5_sub: 'Wees eerlijk - dit is het meest waardevolle dat je invult. Je krijgt hier direct advies op.',
    scan_q5_o1_l: 'Slechte ROAS / te dure klanten',
    scan_q5_o1_d: 'Adverteren kost meer dan het oplevert',
    scan_q5_o2_l: 'Te weinig bereik & naamsbekendheid',
    scan_q5_o2_d: 'Mensen kennen ons merk nog niet',
    scan_q5_o3_l: 'Klanten kopen niet opnieuw',
    scan_q5_o3_d: 'Lage LTV, geen herhaalaankopen',
    scan_q5_o4_l: 'Geen inzicht in wat werkt',
    scan_q5_o4_d: 'Data ontbreekt, beslissingen op gevoel',
    scan_q5_o5_l: 'Geen tijd / geen structuur',
    scan_q5_o5_d: 'Marketing loopt ad-hoc, geen systeem',
    scan_q6_cat: 'Jouw Resultaat',
    scan_q6_title: 'Bijna klaar - waar scannen we?',
    scan_q6_sub: 'Vul je website-URL en e-mail in. Wij scannen je site, socials en ads om een persoonlijk groeirapport samen te stellen.',
    scan_url_label: 'Website URL',
    scan_url_placeholder: 'https://jouwmerk.nl',
    scan_email_label: 'E-mail',
    scan_email_placeholder: 'werk@jouwmerk.nl',
    scan_insight_label: 'WRKT Inzicht',
    scan_back: 'Terug',
    scan_next: 'Volgende',
    scan_submit: 'Bekijk mijn resultaat',
    scan_engine: 'Wrkt Signal™ Engine',
    scan_scanning: 'Scanning',
    scan_patience: 'Even geduld - we analyseren je website, ads en concurrenten.',
    scan_live: 'Live Scan',
    scan_tasks: 'taken',
    scan_step_1: 'Verbinding maken met website...',
    scan_step_2: 'DNS & hosting analyse...',
    scan_step_3: 'Meta Pixel detectie...',
    scan_step_4: 'META Ad Library scan...',
    scan_step_5: 'Creative formats analyseren...',
    scan_step_6: 'Socials scannen...',
    scan_step_7: 'Concurrenten identificeren...',
    scan_step_8: 'Ad Library data ophalen...',
    scan_step_9: 'Funnel & landingspagina analyse...',
    scan_step_10: 'Groei Score berekenen...',
    scan_step_11: 'Rapport genereren...',
    scan_detail_1: 'DNS records ophalen...',
    scan_detail_2: 'Meta Pixel detectie...',
    scan_detail_3: 'Actieve META-campagnes ophalen...',
    scan_detail_4: 'Creative formats analyseren...',
    scan_detail_5: 'Socials scannen...',
    scan_detail_6: 'Concurrenten identificeren in jouw niche...',
    scan_detail_7: 'Ad Library data ophalen...',
    scan_detail_8: 'Funnel & landingspagina analyse...',
    scan_detail_9: 'Groei Score berekenen...',
    scan_detail_10: 'Rapport genereren...',
    scan_complete: 'Scan compleet!',
    scan_connecting: 'Verbinding maken met',
    scan_score_label: 'Jouw WRKT Groei Score',
    scan_score_low: 'Veel potentie, maar fundament mist.',
    scan_score_mid: 'Solide basis, maar er blijft omzet liggen.',
    scan_score_high: 'Sterke basis, klaar voor de volgende stap.',
    scan_report_sent_1: 'We hebben een uitnodiging gestuurd naar ',
    scan_report_sent_2: ' voor een presentatie waarin we in 30 minuten uitleggen welke kansen er liggen.',
    scan_cta_title: 'Wat kun je verwachten in je Growth Review?',
    scan_cta_desc: 'Plan een vrijblijvende Growth Strategy Call. We duiken in je ad-accounts en laten je exact zien waar jouw grootste groeikansen liggen.',
    scan_cta_btn: 'Plan Jouw Strategy Call →',
    scan_back_home: 'Terug naar home',
    scan_hub_cat: 'Categorie',
    scan_hub_revenue: 'Jaaromzet',
    scan_hub_channels: 'Actieve kanalen',
    scan_hub_budget: 'Maandelijks media budget',
    scan_hub_painpoint: 'Grootste pijnpunt',
    scan_hub_score: 'Groei Score',
    scan_d2c_note: 'Specifiek voor D2C brands met €5K+ mediaspend per maand.',
  },
  en: {
    lang_toggle: 'EN / NL',
    nav_cases: 'Cases',
    nav_articles: 'Articles',
    nav_about: 'About us',
    nav_method: 'How we work',
    nav_signal: 'Signal',
    nav_pricing: 'Pricing',
    nav_contact: 'Contact',
    growth_scan_btn: 'Free Growth Scan',
    thanks_title: 'Thank you for your request!',
    thanks_desc: 'We have received your request and will contact you within 24 hours to schedule your Free AI Growth Scan.',
    thanks_sub: 'In the meantime: check out how we, as a digital marketing agency, helped Vedder & Vedder grow by 100% for 5 consecutive years.',
    view_case: 'View the case',
    hero_title_1: 'We grow brands <span class="text-[#5CE698]">&amp;</span> build the future of D2C.',
    hero_title_2: '',
    hero_desc: 'One partner for advertising, CRM, creative and data.',
    hero_desc_bold: 'Wrkt',
    hero_desc_2: 'scales D2C brands with senior expertise and proprietary AI tooling. Predictable and measurable.',
    hero_cta: 'Book an intro call',
    hero_cta2: 'Take the free Growth Scan',
    hero_stat_1: '100+ BRANDS GROWN',
    hero_stat_2: '15 YEARS EXPERIENCE',
    hero_stat_3: '24/7 AI OPTIMIZATION',
    trusted_by: 'These brands chose us',
    prob_label: 'The Problem',
    prob_title_1: "Your ads cost more and deliver less. And you don't know",
    prob_title_2: 'why',
    prob_title_3: '.',
    prob_p1: "CPMs are rising. ROAS is dropping. Your creatives are fatigued. Your team does everything manually while competitors use AI to optimize 24/7. Meanwhile, META's algorithm is becoming increasingly creative-driven - the one with the best creatives wins. Not the one with the highest bid.",
    prob_p2_1: 'Most agencies still operate on the old model: manual management, a monthly report, and hoping for the best.',
    prob_p2_2: 'That no longer works in 2026.',
    fw_label: 'Our Framework',
    fw_title_1: 'The Growth Compass: data-driven growth in 4',
    fw_title_2: 'steps',
    step1_t: 'Scan & Analysis',
    step1_d: 'We dive into your campaigns, creatives and competitors. No assumptions, just facts.',
    step2_t: 'Strategy',
    step2_d: 'We build a growth plan with clear goals, channels and budget allocation.',
    step3_t: 'Execution',
    step3_d: 'Campaigns go live on META, TikTok, Pinterest and more. Our AI optimizes continuously.',
    step4_t: 'Growth & Retention',
    step4_d: 'Weekly reports, new customers and repeat purchases. Growth that sticks.',
    proof_label: 'Proven Results',
    proof_title_1: 'Brands we helped',
    proof_title_2: 'grow.',
    view_all_cases: 'View all cases',
    case_franky_desc: '5-year partner - more than doubled in revenue every year through full-funnel approach.',
    case_feather_desc: 'From zero to market player in a crowded beauty market. D2C launch and sustained growth.',
    case_mimi_desc: 'Amsterdam-based jewelry brand - structurally scaled with data as foundation.',
    case_amaya_desc: '3-year partner - from first online sale to predictable, scalable growth.',
    case_jl_desc: 'Campaign strategy for the Netherlands\' largest legal service provider.',
    kpi_revenue: 'Revenue',
    kpi_new_customers: 'New Customers',
    signal_label: 'Wrkt Signal™',
    signal_title_1_1: 'Your competitor tests 47 creatives a month.',
    signal_title_1_2: 'You test 3. That\'s the',
    signal_title_2: 'difference',
    signal_title_3: '.',
    signal_p1: 'While you plan your next campaign, your competitor already knows what works - and scales it. Our competitive intelligence shows you exactly which creatives, hooks and formats work in your market.',
    signal_p2: '',
    signal_b1_t: 'See what your competitors do',
    signal_b1_d: 'Which ads are they running? Which formats convert? You get instant insight.',
    signal_b2_t: 'AI spots what people miss',
    signal_b2_d: 'Patterns in hooks, visuals and copy that make the difference between 2x and 0.5x ROAS.',
    signal_b3_t: 'Action steps, not reports',
    signal_b3_d: 'Concrete steps you can apply this week. No PowerPoints.',
    cta_badge: 'Ready to grow?',
    cta_title: 'Discover what you\'re leaving on the table.',
    cta_desc: 'Request the free Growth Scan. See where your biggest growth opportunities are, what is happening in your market and which steps will move you forward fastest.',
    faq_title: 'Frequently Asked Questions',
    faq_q1: 'How does Wrkt differ from other agencies?',
    faq_a1: "Wrkt combines senior expertise with proprietary AI tooling. Before we spend a single euro, we analyze your creatives, your competitors' strategies in the Meta Ad Library, and your full funnel. So we start with data. Combined with hands-on expertise across Meta, TikTok, Pinterest, Google Ads and Klaviyo, we build a growth engine tailored to your brand.",
    faq_q2: 'How does the partnership work in practice?',
    faq_a2: "It starts with a 30-minute intro call with a senior strategist who has already looked at your brand. If it fits on both sides, we dive into your numbers and market and come back with a concrete growth plan. Prefer insight first? Start with the free Growth Scan; it analyzes your creatives, competitors and funnel setup and is a strong run-up to the conversation.",
    faq_q3: 'Which channels do you manage?',
    faq_a3: 'We work across the channels that drive the most growth for D2C brands: Meta (Facebook & Instagram), TikTok, Pinterest, Google Ads and Klaviyo for email & retention. We deliberately focus on the channels where your audience is and where we can create the most impact. Which channels make sense for your brand is part of what we cover in the Growth Review.',
    faq_q4: 'How quickly can I expect results?',
    faq_a4: "The first optimizations go live within week one. Most brands see measurable improvements within 30 days and significant growth within 60 to 90 days. The exact timeline depends on your starting point - a brand with strong creatives but poor tracking will see faster wins than one that needs a full funnel rebuild. We're transparent about what to expect and when during your Growth Review.",
    faq_q5: 'Is this suitable for my brand?',
    faq_a5: "Wrkt works exclusively with D2C e-commerce brands that are ready for the next step in growth and advertising. We deliberately keep our client portfolio small, so every client gets real depth and attention. Unsure whether we're a match? The Growth Scan is the best way to find out: free and with no commitment.",
    faq_q6: 'What does the Growth Scan cost?',
    faq_a6: "Nothing. The Growth Scan is completely free. We invest the time in analyzing your creatives, competitors and setup because we only take on brands where we're confident we can make a real impact. Think of it as a way for both sides to find out if there's a fit - no strings attached.",
    faq_q7: 'What does a partnership with Wrkt cost?',
    faq_a7: "Our partnerships start from €2,500 per month, depending on your revenue stage and the number of channels. After your Growth Review we put together a tailored proposal with exactly what your brand needs at this stage. We'd rather do fewer things with real impact than a bit of everything.",
    faq_q8: 'How long is the contract?',
    faq_a8: "We start with a minimum period of three months. That's the time we need to build the foundation - set up tracking, develop a creative strategy, launch campaigns and optimize based on real data. After the initial period, the partnership continues on a monthly basis. We believe in earning your trust every month, not locking you in.",
    footer_desc: 'Growth agency for D2C e-commerce. Senior expertise, proprietary AI tooling. Amsterdam. Meta Business Partner.',
    footer_partners: 'Official Partners',
    footer_contact: 'Contact',
    footer_links: 'Links',
    footer_cases: 'Cases',
    footer_privacy: 'Privacy Policy',
    footer_rights: 'All rights reserved.',
    contact_title: 'Talk to Robin, not sales',
    contact_desc: 'You speak directly with Robin de Wildt: we discuss your challenges, look at your numbers and figure out together if there\'s a fit.',
    contact_info_title: 'Book directly',
    contact_address: 'Herengracht 372, 1016 CH Amsterdam',
    contact_email: 'contact@wrkt.nl',
    contact_phone: '+31 (0)20 896 51 36',
    contact_form_name: 'Name',
    contact_form_email: 'Business Email',
    contact_form_company: 'Company Name or Website',
    contact_form_budget: 'Where did you hear about us?',
    contact_form_message: 'Message (Optional)',
    contact_form_submit: 'Send Request',
    contact_success_title: 'Thank you for your request',
    contact_success_desc: 'We will get back to you as soon as possible.',
    contact_step1_t: '1. Discovery Call',
    contact_step1_d: 'We discuss your current e-commerce goals, obstacles, and see if there is a match.',
    contact_step2_t: '2. Account Audit',
    contact_step2_d: 'You give us access, we make an in-depth analysis of your campaigns and market opportunities.',
    contact_step3_t: '3. Strategy Session',
    contact_step3_d: 'We present our findings and the concrete steps for higher ROAS.',
    contact_step4_t: '4. Proposal',
    contact_step4_d: 'You receive a clear action plan to achieve your goals with our dedicated team.',
    contact_step5_t: '5. Launch & Scale',
    contact_step5_d: 'Upon agreement, we launch the new campaigns and start the 24/7 AI optimization.',
    about_title_1: 'Meet',
    about_title_2: 'Wrkt Digital',
    about_subtitle: 'Experts in strategically growing D2C brands.',
    about_p1: 'Wrkt Digital was founded on one conviction: growth should be predictable and measurable. We combine 10 years of Wrkt and 15+ years of performance expertise with proprietary AI tooling and a senior team that knows e-commerce inside out.',
    about_p2: 'You work directly with your strategist, follow your campaigns daily in your own dashboard, and know exactly what the next step is every month.',
    about_stat1_val: '2017',
    about_stat1_label: 'FOUNDED',
    about_stat2_val: '5',
    about_stat2_label: 'SPECIALISTS',
    about_stat3_val: '100+',
    about_stat3_label: 'BRANDS HELPED',
    about_stat4_val: '0',
    about_stat4_label: 'ACCOUNT MANAGERS',
    about_team_title: 'Our team',
    about_team_desc: 'Small, fast and hands-on. We work from the Herengracht in Amsterdam. Everyone on the team works directly on client campaigns - no layers in between.',
    about_robin_role: 'Founder & Growth Strategist',
    about_robin_desc: '15 years in e-commerce. Has helped 100+ brands grow. Thinks in data, acts on opportunity.',
    about_team_member2: 'Performance Marketing',
    about_team_member3: 'Creative Strategy',
    about_team_member4: 'AI & Data Engineering',
    about_location_title: 'Herengracht, Amsterdam',
    about_location_desc: 'Our office is at Herengracht 372 in Amsterdam. Feel free to drop by for a coffee and a good conversation about growth.',
    about_cta: 'Get in touch',
    scan_landing_badge: '2 minutes · Free · Instant results',
    scan_landing_title_1: 'How far is',
    scan_landing_title_2: 'your brand',
    scan_landing_title_3: 'from real',
    scan_landing_title_4: 'growth?',
    scan_landing_desc_1: 'Take the ',
    scan_landing_desc_bold: 'WRKT Growth Scan',
    scan_landing_desc_2: ': 5 questions about your brand + an instant scan of your website for SEO, socials, tracking and technical setup.',
    scan_landing_list_1: 'SEO & meta tags check of your website',
    scan_landing_list_2: 'Social media channels & tracking pixels detection',
    scan_landing_list_3: 'Technical setup assessment',
    scan_landing_list_4: 'Personal growth score with improvement points',
    scan_start_btn: 'Start the Growth Scan',
    scan_q1_cat: 'Your Brand',
    scan_q1_title: 'What category is your brand in?',
    scan_q1_sub: 'We specialize in lifestyle verticals - this way we give you the most relevant benchmarks.',
    scan_q1_o1_l: 'Fashion & Jewelry',
    scan_q1_o1_d: 'Clothing, accessories, jewelry, watches',
    scan_q1_o2_l: 'Beauty & Care',
    scan_q1_o2_d: 'Cosmetics, skincare, haircare, wellness',
    scan_q1_o3_l: 'Home & Living',
    scan_q1_o3_d: 'Interior, home decor, lifestyle products',
    scan_q1_o4_l: 'Food & Health',
    scan_q1_o4_d: 'Nutrition, supplements, healthy lifestyle',
    scan_q1_o5_l: 'Other D2C category',
    scan_q1_o5_d: 'Direct-to-consumer, but different',
    scan_q2_cat: 'Revenue & Ambition',
    scan_q2_title: 'What is your current annual revenue?',
    scan_q2_sub: 'Honesty pays off - we tailor our advice to where you truly stand.',
    scan_q2_o1_l: 'Under €100K',
    scan_q2_o1_d: 'Early stage, building initial traction',
    scan_q2_o2_l: '€100K – €500K',
    scan_q2_o2_d: 'Growing base, first scaling experiences',
    scan_q2_o3_l: '€500K – €2M',
    scan_q2_o3_d: 'Serious traction, ready to accelerate',
    scan_q2_o4_l: '€2M – €10M',
    scan_q2_o4_d: 'Established brand, scaling is the goal',
    scan_q2_o5_l: 'Above €10M',
    scan_q2_o5_d: 'Enterprise-level, further growth',
    scan_q2_insight: 'Most lifestyle brands scale fastest between €500K and €5M annual revenue. This is the moment when paid advertising has the greatest leverage effect.',
    scan_q3_cat: 'Channels',
    scan_q3_title: 'Which channels are you currently using?',
    scan_q3_sub: 'Select everything you\'re currently using. We\'ll then look at what\'s missing.',
    scan_q3_multi_hint: 'Multiple answers possible.',
    scan_q3_o1_l: 'Meta Ads',
    scan_q3_o1_d: 'Facebook & Instagram advertising',
    scan_q3_o2_l: 'Google Ads',
    scan_q3_o2_d: 'Search, Shopping, Performance Max',
    scan_q3_o3_l: 'TikTok Ads',
    scan_q3_o3_d: 'Short-form video advertising',
    scan_q3_o4_l: 'Pinterest Ads',
    scan_q3_o4_d: 'Visual discovery platform',
    scan_q3_o5_l: 'Email / Klaviyo',
    scan_q3_o5_d: 'Newsletters, flows, automations',
    scan_q3_o6_l: 'Influencer / UGC',
    scan_q3_o6_d: 'Creator content & partnerships',
    scan_q3_o7_l: 'No paid yet',
    scan_q3_o7_d: 'Organic only or not active yet',
    scan_q4_cat: 'Media Budget',
    scan_q4_title: 'How much do you invest monthly in paid media?',
    scan_q4_sub: 'Your ad budget determines which strategy and scaling speed is realistic for you.',
    scan_q4_o1_l: 'Less than €1,000',
    scan_q4_o1_d: 'First steps, still testing',
    scan_q4_o2_l: '€1,000 – €5,000',
    scan_q4_o2_d: 'Actively testing, results are coming',
    scan_q4_o3_l: '€5,000 – €20,000',
    scan_q4_o3_d: 'Serious budget, ROI expected',
    scan_q4_o4_l: '€20,000 – €75,000',
    scan_q4_o4_d: 'Major player, scaling ambitions',
    scan_q4_o5_l: 'Above €75,000',
    scan_q4_o5_d: 'Enterprise-level investment',
    scan_q4_insight: 'With less than €1K/month it\'s difficult to collect significant data. Brands that truly scale start at €3–5K/month as minimum spend.',
    scan_q5_cat: 'Pain Point',
    scan_q5_title: 'What is holding your growth back the most?',
    scan_q5_sub: 'Be honest - this is the most valuable thing you fill in. You\'ll get direct advice on this.',
    scan_q5_o1_l: 'Poor ROAS / expensive customers',
    scan_q5_o1_d: 'Advertising costs more than it returns',
    scan_q5_o2_l: 'Too little reach & brand awareness',
    scan_q5_o2_d: 'People don\'t know our brand yet',
    scan_q5_o3_l: 'Customers don\'t buy again',
    scan_q5_o3_d: 'Low LTV, no repeat purchases',
    scan_q5_o4_l: 'No insight into what works',
    scan_q5_o4_d: 'Data is missing, decisions based on gut feeling',
    scan_q5_o5_l: 'No time / no structure',
    scan_q5_o5_d: 'Marketing runs ad-hoc, no system',
    scan_q6_cat: 'Your Result',
    scan_q6_title: 'Almost done - where should we scan?',
    scan_q6_sub: 'Enter your website URL and email. We\'ll scan your site, socials and ads to create a personalized growth report.',
    scan_url_label: 'Website URL',
    scan_url_placeholder: 'https://yourbrand.com',
    scan_email_label: 'Email',
    scan_email_placeholder: 'work@yourbrand.com',
    scan_insight_label: 'WRKT Insight',
    scan_back: 'Back',
    scan_next: 'Next',
    scan_submit: 'View my result',
    scan_engine: 'Wrkt Signal™ Engine',
    scan_scanning: 'Scanning',
    scan_patience: 'Please wait - we\'re analyzing your website, ads and competitors.',
    scan_live: 'Live Scan',
    scan_tasks: 'tasks',
    scan_step_1: 'Connecting to website...',
    scan_step_2: 'DNS & hosting analysis...',
    scan_step_3: 'Meta Pixel detection...',
    scan_step_4: 'META Ad Library scan...',
    scan_step_5: 'Analyzing creative formats...',
    scan_step_6: 'Scanning socials...',
    scan_step_7: 'Identifying competitors...',
    scan_step_8: 'Fetching Ad Library data...',
    scan_step_9: 'Funnel & landing page analysis...',
    scan_step_10: 'Calculating Growth Score...',
    scan_step_11: 'Generating report...',
    scan_detail_1: 'Fetching DNS records...',
    scan_detail_2: 'Meta Pixel detection...',
    scan_detail_3: 'Fetching active META campaigns...',
    scan_detail_4: 'Analyzing creative formats...',
    scan_detail_5: 'Scanning socials...',
    scan_detail_6: 'Identifying competitors in your niche...',
    scan_detail_7: 'Fetching Ad Library data...',
    scan_detail_8: 'Funnel & landing page analysis...',
    scan_detail_9: 'Calculating Growth Score...',
    scan_detail_10: 'Generating report...',
    scan_complete: 'Scan complete!',
    scan_connecting: 'Connecting to',
    scan_score_label: 'Your WRKT Growth Score',
    scan_score_low: 'Lots of potential, but the foundation is missing.',
    scan_score_mid: 'A solid base, but revenue is being left on the table.',
    scan_score_high: 'A strong foundation, ready for the next step.',
    scan_report_sent_1: 'We\'ve sent an invitation to ',
    scan_report_sent_2: ' for a presentation where we explain in 30 minutes which opportunities are available.',
    scan_cta_title: 'What to expect in your Growth Review?',
    scan_cta_desc: 'Schedule a no-obligation Growth Strategy Call. We\'ll dive into your ad accounts and show you exactly where your biggest growth opportunities are.',
    scan_cta_btn: 'Schedule Your Strategy Call →',
    scan_back_home: 'Back to home',
    scan_hub_cat: 'Category',
    scan_hub_revenue: 'Annual revenue',
    scan_hub_channels: 'Active channels',
    scan_hub_budget: 'Monthly media budget',
    scan_hub_painpoint: 'Biggest pain point',
    scan_hub_score: 'Growth Score',
    scan_d2c_note: 'Specifically for D2C brands with €5K+ media spend per month.',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getLanguageFromPath(): Language {
  return window.location.pathname.startsWith('/en') ? 'en' : 'nl';
}

function getPathWithoutLang(pathname: string): string {
  if (pathname.startsWith('/en/')) return pathname.slice(3) || '/';
  if (pathname === '/en') return '/';
  return pathname;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(getLanguageFromPath);

  useEffect(() => {
    const handlePopState = () => {
      setLanguage(getLanguageFromPath());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'nl' ? 'en' : 'nl';
    const basePath = getPathWithoutLang(window.location.pathname);
    const newPath = newLang === 'en' ? `/en${basePath === '/' ? '' : basePath}` || '/en' : basePath;
    window.history.pushState({}, '', newPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
    setLanguage(newLang);
  };

  const t = (key: string) => {
    return translations[language]?.[key] || key;
  };

  const localePath = (path: string) => {
    // Idempotent: paden die al een /en-prefix hebben niet nogmaals prefixen (voorkomt /en/en/-404's).
    if (path === '/en' || path.startsWith('/en/')) return path;
    if (language === 'en') {
      return path === '/' ? '/en' : `/en${path}`;
    }
    return path;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t, localePath }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}